import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Building2, BellRing, ShieldCheck, KeyRound, Save, BookOpen, Code2, CreditCard, Eye, Send, Image as ImageIcon, Upload, Trash2, Sparkles, Search } from "lucide-react";
import EmailPreviewGallery from "@/components/EmailPreviewGallery";
import { openEmployerManual, openAdminManual } from "@/lib/userManual";
import ManualSearchModal from "@/components/ManualSearchModal";
import EmbedSnippet from "@/components/EmbedSnippet";
import CalendarConnectCard from "@/components/CalendarConnectCard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const REMINDER_OPTIONS = [
  { value: "24", label: "24 hours" },
  { value: "48", label: "48 hours (default)" },
  { value: "72", label: "72 hours" },
  { value: "120", label: "5 days" },
  { value: "168", label: "1 week" },
];

const DEFAULT_BODY_NOT_HIRED =
  "Thanks for your interest in {position} at {employer}. After careful review, the team has decided to move forward with other candidates this time.\n\nWe'll keep your information on file for future openings at {employer} and wish you the best in your search.";

const DEFAULT_BODY_NOT_INTERESTED =
  "Per your request, we've withdrawn your application for {position} at {employer}.\n\nYou're welcome to apply again any time. Thanks for considering {employer}.";

const MAX_REMINDER_OPTIONS = [
  { value: "0", label: "Off (don't send reminders)" },
  { value: "1", label: "1 reminder" },
  { value: "2", label: "2 reminders (default)" },
  { value: "3", label: "3 reminders" },
];

const PLAN_LABELS = {
  starter: "Starter — $39/mo",
  pro: "Pro — $99/mo",
  enterprise: "Enterprise — $299/mo",
};

export default function Settings() {
  const { user, refresh } = useAuth();
  const [manualSearchKind, setManualSearchKind] = useState(null);
  const [form, setForm] = useState({
    name: "",
    company: "",
    default_reminder_hours: "48",
    max_reminders: "2",
    password: "",
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [notifyPaid, setNotifyPaid] = useState(false);
  const [notifyFailed, setNotifyFailed] = useState(false);
  const [notifyCc, setNotifyCc] = useState("");
  const [savingNotify, setSavingNotify] = useState(false);
  const [emailNotHired, setEmailNotHired] = useState(false);
  const [emailNotInterested, setEmailNotInterested] = useState(false);
  const [bodyNotHired, setBodyNotHired] = useState("");
  const [bodyNotInterested, setBodyNotInterested] = useState("");
  const [savingCandidateEmails, setSavingCandidateEmails] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [testSending, setTestSending] = useState(null); // 'not_hired' | 'not_interested' | null
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [digestDay, setDigestDay] = useState("0");
  const [savingDigest, setSavingDigest] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [hidePoweredBy, setHidePoweredBy] = useState(false);
  const [brandLabel, setBrandLabel] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [sendingReplyToTest, setSendingReplyToTest] = useState(false);
  const [savingBrand, setSavingBrand] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [welcomePack, setWelcomePack] = useState(true);
  const [previewGalleryOpen, setPreviewGalleryOpen] = useState(false);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: user.name || "",
        company: user.company || "",
        default_reminder_hours: String(user.default_reminder_hours || 48),
        max_reminders: String(
          user.max_reminders === null || user.max_reminders === undefined
            ? 2
            : user.max_reminders
        ),
      }));
      setNotifyPaid(!!user.billing_notify_paid);
      setNotifyFailed(!!user.billing_notify_failed);
      setNotifyCc((user.billing_notify_cc || []).join(", "));
      setEmailNotHired(!!user.auto_email_not_hired);
      setEmailNotInterested(!!user.auto_email_not_interested);
      setBodyNotHired(user.email_body_not_hired || "");
      setBodyNotInterested(user.email_body_not_interested || "");
      setDigestEnabled(!!user.stale_digest_enabled);
      setDigestDay(String(user.stale_digest_day_utc ?? 0));
      setHidePoweredBy(!!user.hide_powered_by);
      setBrandLabel(user.brand_company_label || user.company || "");
      setReplyTo(user.reply_to_email || "");
      setWelcomePack(user.welcome_pack_enabled !== false);
    }
  }, [user]);

  const saveBrand = async (e) => {
    e.preventDefault();
    setSavingBrand(true);
    try {
      const payload = {
        brand_company_label: brandLabel,
        reply_to_email: replyTo,
      };
      if (user?.team_owner_id === user?.id) {
        // Owner-only fields: only send them when the current user is the team owner
        payload.hide_powered_by = hidePoweredBy;
        payload.welcome_pack_enabled = welcomePack;
      }
      await api.patch("/auth/me", payload);
      await refresh();
      toast.success("Branding saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save branding");
    } finally {
      setSavingBrand(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Logo must be ≤ 500 KB");
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/auth/me/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refresh();
      toast.success("Logo uploaded — refresh candidate-facing pages to see it");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo upload failed");
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = async () => {
    setUploadingLogo(true);
    try {
      await api.delete("/auth/me/logo");
      await refresh();
      toast.success("Logo removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const sendReplyToTest = async () => {
    if (!user?.reply_to_email) {
      toast.error("Save a Reply-To address first.");
      return;
    }
    setSendingReplyToTest(true);
    try {
      const r = await api.post("/email-templates/test-send", {
        kind: "invite",
        to_reply_to: true,
      });
      toast.success(`Test invite sent to ${r.data?.sent_to || user.reply_to_email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send test email");
    } finally {
      setSendingReplyToTest(false);
    }
  };

  const saveDigest = async (e) => {
    e.preventDefault();
    setSavingDigest(true);
    try {
      await api.patch("/auth/me", {
        stale_digest_enabled: digestEnabled,
        stale_digest_day_utc: parseInt(digestDay, 10),
      });
      await refresh();
      toast.success("Weekly digest settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save digest settings");
    } finally {
      setSavingDigest(false);
    }
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/digest/send-now", {
        app_base: window.location.origin,
      });
      toast.success(
        data.total === 0
          ? "Sent — your pipeline is healthy, no stale candidates this week."
          : `Sent — ${data.total} stale candidate${data.total === 1 ? "" : "s"} in this week's digest`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send digest");
    } finally {
      setSendingDigest(false);
    }
  };

  const saveNotify = async (e) => {
    e.preventDefault();
    setSavingNotify(true);
    try {
      const cc = notifyCc
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s && s.includes("@"));
      await api.patch("/auth/me", {
        billing_notify_paid: notifyPaid,
        billing_notify_failed: notifyFailed,
        billing_notify_cc: cc,
      });
      await refresh();
      toast.success("Billing notifications saved");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not save preferences"
      );
    } finally {
      setSavingNotify(false);
    }
  };

  const saveCandidateEmails = async (e) => {
    e.preventDefault();
    setSavingCandidateEmails(true);
    try {
      await api.patch("/auth/me", {
        auto_email_not_hired: emailNotHired,
        auto_email_not_interested: emailNotInterested,
        email_body_not_hired: bodyNotHired,
        email_body_not_interested: bodyNotInterested,
      });
      await refresh();
      toast.success("Candidate email preferences saved");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not save preferences"
      );
    } finally {
      setSavingCandidateEmails(false);
    }
  };

  const previewEmail = async (kind) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const body = kind === "not_hired" ? bodyNotHired : bodyNotInterested;
      const { data } = await api.post("/applicants/email-templates/preview", {
        kind,
        body,
      });
      setPreviewData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not preview email");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const testSendEmail = async (kind) => {
    setTestSending(kind);
    try {
      const body = kind === "not_hired" ? bodyNotHired : bodyNotInterested;
      const { data } = await api.post(
        "/applicants/email-templates/test-send",
        { kind, body }
      );
      toast.success(`Test email sent to ${data.sent_to}`);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not send test email"
      );
    } finally {
      setTestSending(null);
    }
  };

  if (!user) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-12 text-slate-500">
        Loading…
      </main>
    );
  }

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const payload = {};
      if (form.name !== (user.name || "")) payload.name = form.name.trim();
      if (form.company !== (user.company || ""))
        payload.company = form.company.trim();
      const newCadence = parseInt(form.default_reminder_hours, 10);
      if (newCadence !== (user.default_reminder_hours || 48)) {
        payload.default_reminder_hours = newCadence;
      }
      const newMax = parseInt(form.max_reminders, 10);
      const currentMax =
        user.max_reminders === null || user.max_reminders === undefined
          ? 2
          : user.max_reminders;
      if (newMax !== currentMax) {
        payload.max_reminders = newMax;
      }
      if (Object.keys(payload).length === 0) {
        toast.info("No changes to save");
        return;
      }
      await api.patch("/auth/me", payload);
      await refresh();
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save settings");
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (!form.password || form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSavingPassword(true);
    try {
      const { data } = await api.patch("/auth/me", {
        password: form.password,
      });
      // Backend rotates token_version on password change; keep session alive
      if (data?.token) {
        localStorage.setItem("aip_token", data.token);
        await refresh();
      }
      setForm({ ...form, password: "" });
      toast.success("Password updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password");
    } finally {
      setSavingPassword(false);
    }
  };

  const planLabel = user.subscription_plan
    ? PLAN_LABELS[user.subscription_plan] || user.subscription_plan
    : "No active plan";
  const activeUntil = user.subscription_active_until
    ? new Date(user.subscription_active_until).toLocaleDateString()
    : null;

  return (
    <>
    <main data-testid="settings-page" className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-12">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
          Account
        </div>
        <h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
          Settings
        </h1>
        <p className="mt-3 max-w-xl text-sm text-slate-900">
          Update how Hireloop PRO shows up to candidates, and choose how
          aggressively we nudge people who haven't responded.
        </p>
      </div>

      {/* Profile */}
      <form
        onSubmit={saveProfile}
        data-testid="settings-profile-form"
        className="mt-10 border border-slate-200"
      >
        <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
          <Building2 className="h-3 w-3" />
          Profile & defaults
        </div>
        <div className="space-y-5 p-5">
          <div className="grid gap-2">
            <Label htmlFor="settings-name">Display name</Label>
            <Input
              id="settings-name"
              data-testid="settings-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="settings-company">Company name</Label>
            <Input
              id="settings-company"
              data-testid="settings-company-input"
              value={form.company}
              placeholder="Acme Inc."
              onChange={(e) =>
                setForm({ ...form, company: e.target.value })
              }
            />
            <p className="text-xs text-slate-900">
              Shown to candidates in your interview invites and reminder
              emails.
            </p>
          </div>
          <div className="grid gap-2">
            <Label className="flex items-center gap-2">
              <BellRing className="h-3.5 w-3.5" />
              Default reminder cadence
            </Label>
            <Select
              value={form.default_reminder_hours}
              onValueChange={(v) =>
                setForm({ ...form, default_reminder_hours: v })
              }
            >
              <SelectTrigger data-testid="settings-cadence-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REMINDER_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-900">
              How long we wait before automatically sending a reminder email
              to candidates who haven't started their interview.
            </p>
          </div>

          <div className="grid gap-2">
            <Label className="flex items-center gap-2">
              <BellRing className="h-3.5 w-3.5" />
              Maximum reminders per candidate
            </Label>
            <Select
              value={form.max_reminders}
              onValueChange={(v) => setForm({ ...form, max_reminders: v })}
            >
              <SelectTrigger data-testid="settings-max-reminders-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MAX_REMINDER_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-900">
              Cap on how many nudges a single non-responder receives. Each
              reminder is spaced by your cadence above. Choose <em>Off</em> to
              disable automatic + manual reminders entirely.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3">
          <Button
            type="submit"
            disabled={savingProfile}
            data-testid="settings-save-btn"
            className="bg-brand-gradient rounded-sm text-white"
            size="sm"
          >
            <Save className="mr-2 h-3.5 w-3.5" />
            {savingProfile ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>

      {/* Branding / White-label */}
      <form
        data-testid="settings-branding-card"
        onSubmit={saveBrand}
        className="mt-8 border border-slate-200"
      >
        <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
          <ImageIcon className="h-3 w-3" />
          Branding & white-label
        </div>
        <div className="space-y-5 p-5">
          <p className="text-sm text-slate-600">
            Upload your company logo and (optionally) hide all Hireloop PRO branding from your
            candidate-facing pages and emails. White-label changes apply to: the public careers
            page, the application form, the application status check, tokenized résumé-upload
            links, and the weekly digest action confirmation page.
            {" "}
            <span className="text-slate-500">
              Logo + brand label are a <strong>shared team brand</strong> — any teammate on your
              account can update them. The white-label and Welcome Pack toggles stay owner-only.
            </span>
          </p>

          {/* Logo */}
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.16em] text-slate-700">
              Company logo
            </Label>
            <div className="mt-2 flex items-center gap-4">
              <div
                data-testid="settings-logo-preview"
                className="flex h-20 w-20 flex-none items-center justify-center overflow-hidden rounded-sm border border-dashed border-slate-300 bg-slate-50"
              >
                {user?.logo_url ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${user.logo_url}`}
                    alt="Company logo preview"
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <ImageIcon className="h-6 w-6 text-slate-300" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input
                  type="file"
                  data-testid="settings-logo-input"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
                  onChange={(e) => uploadLogo(e.target.files?.[0])}
                  className="hidden"
                  id="logo-upload"
                  disabled={uploadingLogo}
                />
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    data-testid="settings-logo-upload-btn"
                    variant="outline"
                    size="sm"
                    disabled={uploadingLogo}
                    onClick={() => document.getElementById("logo-upload")?.click()}
                  >
                    <Upload className="mr-2 h-3.5 w-3.5" />
                    {user?.logo_url ? "Replace logo" : "Upload logo"}
                  </Button>
                  {user?.logo_url && (
                    <Button
                      type="button"
                      data-testid="settings-logo-remove-btn"
                      variant="ghost"
                      size="sm"
                      disabled={uploadingLogo}
                      onClick={removeLogo}
                      className="text-rose-700 hover:bg-rose-50 hover:text-rose-900"
                    >
                      <Trash2 className="mr-2 h-3.5 w-3.5" />
                      Remove
                    </Button>
                  )}
                </div>
                <p className="text-[11px] text-slate-500">
                  PNG, JPG, SVG, WebP, or GIF · max 500 KB · square or wide both work.
                </p>
              </div>
            </div>
          </div>

          {/* Brand label */}
          <div>
            <Label
              htmlFor="brand-label"
              className="text-xs font-bold uppercase tracking-[0.16em] text-slate-700"
            >
              Brand label (shown to candidates)
            </Label>
            <Input
              id="brand-label"
              data-testid="settings-brand-label"
              value={brandLabel}
              onChange={(e) => setBrandLabel(e.target.value)}
              placeholder={user?.company || "Your company name"}
              maxLength={80}
              className="mt-1.5"
            />
            <p className="mt-1 text-[11px] text-slate-500">
              Falls back to your company name if left blank. Used in candidate emails and on the
              careers page header.
            </p>
          </div>

          {/* Reply-To address */}
          <div>
            <Label
              htmlFor="reply-to-email"
              className="text-xs font-bold uppercase tracking-[0.16em] text-slate-700"
            >
              Reply-to address (optional)
            </Label>
            <div className="mt-1.5 flex flex-col gap-2 sm:flex-row sm:items-stretch">
              <Input
                id="reply-to-email"
                data-testid="settings-reply-to-email"
                type="email"
                value={replyTo}
                onChange={(e) => setReplyTo(e.target.value)}
                placeholder="recruiting@yourcompany.com"
                maxLength={200}
                className="flex-1"
              />
              <Button
                type="button"
                data-testid="settings-reply-to-test-btn"
                variant="outline"
                size="sm"
                disabled={!user?.reply_to_email || sendingReplyToTest}
                onClick={sendReplyToTest}
                className="rounded-sm whitespace-nowrap"
                title={
                  !user?.reply_to_email
                    ? "Save a Reply-To address first, then click to send a real test invite to that inbox."
                    : `Send a sample invite to ${user.reply_to_email}`
                }
              >
                <Send className="mr-2 h-3.5 w-3.5" />
                {sendingReplyToTest ? "Sending…" : "Send test to Reply-To"}
              </Button>
            </div>
            <p className="mt-1 text-[11px] text-slate-500">
              When candidates hit <strong>Reply</strong> on your invites, reminders, welcome
              packs and status emails, their replies will route here instead of bouncing back to
              our <code>hello@</code> sender. Leave blank to disable.
              {" "}
              <span className="text-slate-400">
                The test button fires a sample invite to your saved Reply-To address so you can
                confirm it arrives.
              </span>
            </p>
          </div>

          {/* Whitelabel toggle */}
          <label className={`flex items-start gap-3 ${user?.team_owner_id !== user?.id ? "opacity-60" : ""}`}>
            <input
              type="checkbox"
              data-testid="settings-hide-powered-by-toggle"
              checked={hidePoweredBy}
              onChange={(e) => setHidePoweredBy(e.target.checked)}
              disabled={user?.team_owner_id !== user?.id}
              className="mt-1 h-4 w-4 rounded-sm border-slate-300 text-blue-600 disabled:cursor-not-allowed"
            />
            <span className="text-sm">
              <span className="font-semibold text-slate-900">
                Hide all Hireloop PRO branding
              </span>
              <span className="ml-2 text-xs text-slate-500">
                (removes the wordmark + "Powered by Hireloop" footers from all candidate-facing
                pages and emails)
              </span>
              {user?.team_owner_id !== user?.id && (
                <span
                  data-testid="settings-whitelabel-owner-only"
                  className="ml-2 inline-block rounded-sm bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-700"
                >
                  Owner only
                </span>
              )}
            </span>
          </label>

          {/* Welcome Pack toggle */}
          <label className={`flex items-start gap-3 ${user?.team_owner_id !== user?.id ? "opacity-60" : ""}`}>
            <input
              type="checkbox"
              data-testid="settings-welcome-pack-toggle"
              checked={welcomePack}
              onChange={(e) => setWelcomePack(e.target.checked)}
              disabled={user?.team_owner_id !== user?.id}
              className="mt-1 h-4 w-4 rounded-sm border-slate-300 text-blue-600 disabled:cursor-not-allowed"
            />
            <span className="text-sm">
              <span className="font-semibold text-slate-900">
                Send candidates a Welcome Pack on application
              </span>
              <span className="ml-2 text-xs text-slate-500">
                (auto-emails an application-received confirmation with their APP-YYYY-NNNNN
                reference, status link, expected timeline, and one-click "Withdraw" — fully
                branded with your logo + label · default on)
              </span>
              {user?.team_owner_id !== user?.id && (
                <span
                  data-testid="settings-welcome-owner-only"
                  className="ml-2 inline-block rounded-sm bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-700"
                >
                  Owner only
                </span>
              )}
            </span>
          </label>
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3">
          <Button
            type="button"
            data-testid="settings-preview-emails-btn"
            variant="outline"
            size="sm"
            onClick={() => setPreviewGalleryOpen(true)}
            className="rounded-sm"
          >
            <Sparkles className="mr-2 h-3.5 w-3.5" />
            Preview emails
          </Button>
          <Button
            type="submit"
            data-testid="settings-brand-save-btn"
            disabled={savingBrand}
            size="sm"
            className="bg-brand-gradient rounded-sm text-white"
          >
            <Save className="mr-2 h-3.5 w-3.5" />
            {savingBrand ? "Saving…" : "Save branding"}
          </Button>
        </div>
      </form>

      <EmailPreviewGallery
        open={previewGalleryOpen}
        onOpenChange={setPreviewGalleryOpen}
      />

      {/* Google Calendar */}
      <CalendarConnectCard />

      {/* Weekly stale-pipeline digest */}
      <form
        data-testid="settings-digest-card"
        onSubmit={saveDigest}
        className="mt-8 border border-slate-200"
      >
        <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
          <BellRing className="h-3 w-3" />
          Weekly stale-pipeline digest
        </div>
        <div className="space-y-5 p-5">
          <p className="text-sm text-slate-600">
            Opt in to a once-a-week email summarising your stalest candidates per stage —
            with one-click <b>Nudge all</b> and <b>Move to Not Interested</b> buttons right
            inside the email. Recipient is always the team owner's email on file.
          </p>
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              data-testid="digest-enabled-toggle"
              checked={digestEnabled}
              onChange={(e) => setDigestEnabled(e.target.checked)}
              className="mt-1 h-4 w-4 rounded-sm border-slate-300 text-blue-600"
            />
            <span className="text-sm">
              <span className="font-semibold text-slate-900">
                Send me the weekly digest
              </span>
              <span className="ml-2 text-xs text-slate-500">
                (auto-skipped if your pipeline is clean)
              </span>
            </span>
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.16em] text-slate-700">
                Day of week
              </Label>
              <Select value={digestDay} onValueChange={setDigestDay}>
                <SelectTrigger
                  data-testid="digest-day-select"
                  className="mt-1.5"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Monday</SelectItem>
                  <SelectItem value="1">Tuesday</SelectItem>
                  <SelectItem value="2">Wednesday</SelectItem>
                  <SelectItem value="3">Thursday</SelectItem>
                  <SelectItem value="4">Friday</SelectItem>
                  <SelectItem value="5">Saturday</SelectItem>
                  <SelectItem value="6">Sunday</SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1.5 text-[11px] text-slate-500">
                Digests are sent after 09:00 UTC on your chosen day.
              </p>
            </div>
            <div className="flex flex-col justify-end">
              {user?.stale_digest_last_sent_at && (
                <p className="text-[11px] text-slate-500">
                  Last digest sent:{" "}
                  <span className="font-mono text-slate-800">
                    {new Date(user.stale_digest_last_sent_at).toLocaleString()}
                  </span>
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3">
          <Button
            type="button"
            data-testid="digest-send-now-btn"
            onClick={sendDigestNow}
            disabled={sendingDigest}
            variant="outline"
            size="sm"
            className="rounded-sm"
          >
            <Send className="mr-2 h-3.5 w-3.5" />
            {sendingDigest ? "Sending…" : "Send digest now"}
          </Button>
          <Button
            type="submit"
            data-testid="digest-save-btn"
            disabled={savingDigest}
            size="sm"
            className="bg-brand-gradient rounded-sm text-white"
          >
            <Save className="mr-2 h-3.5 w-3.5" />
            {savingDigest ? "Saving…" : "Save digest settings"}
          </Button>
        </div>
      </form>

      {/* Plan */}
      <div
        data-testid="settings-plan-card"
        className="mt-8 border border-slate-200"
      >
        <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
          <ShieldCheck className="h-3 w-3" />
          Subscription
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 p-5">
          <div>
            <div
              data-testid="settings-plan-label"
              className="font-heading text-base font-medium text-slate-900"
            >
              {planLabel}
            </div>
            {activeUntil && (
              <div className="mt-1 text-xs text-slate-900">
                Active through {activeUntil}
              </div>
            )}
          </div>
          <Button
            asChild
            size="sm"
            className="rounded-sm bg-red-600 text-white hover:bg-red-700"
            data-testid="settings-manage-plan-btn"
          >
            <a href="/billing">Manage plan</a>
          </Button>
        </div>
      </div>

      {/* User manuals */}
      <div
        data-testid="settings-manuals-card"
        className="mt-8 border border-slate-200"
      >
        <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
          <BookOpen className="h-3 w-3" />
          User manuals
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 p-5">
          <p className="text-sm text-slate-900">
            Search the manual in-app or download a print-ready PDF.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              data-testid="search-employer-manual-btn"
              onClick={() => setManualSearchKind("employer")}
              className="rounded-sm border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              <Search className="mr-2 h-3.5 w-3.5" />
              Search Employer Manual
            </Button>
            {user.role === "admin" && (
              <Button
                size="sm"
                variant="outline"
                data-testid="search-admin-manual-btn"
                onClick={() => setManualSearchKind("admin")}
                className="rounded-sm border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                <Search className="mr-2 h-3.5 w-3.5" />
                Search Admin Manual
              </Button>
            )}
            <Button
              size="sm"
              data-testid="open-employer-manual-btn"
              onClick={async () => {
                const t = toast.loading("Generating PDF…");
                try {
                  await openEmployerManual();
                  toast.success("Employer Manual downloaded", { id: t });
                } catch (e) {
                  console.error(e);
                  toast.error("Failed to generate PDF", { id: t });
                }
              }}
              className="rounded-sm bg-blue-600 text-white hover:bg-blue-700"
            >
              <BookOpen className="mr-2 h-3.5 w-3.5" />
              Employer Manual
            </Button>
            {user.role === "admin" && (
              <Button
                size="sm"
                data-testid="open-admin-manual-btn"
                onClick={async () => {
                  const t = toast.loading("Generating PDF…");
                  try {
                    await openAdminManual();
                    toast.success("Admin Manual downloaded", { id: t });
                  } catch (e) {
                    console.error(e);
                    toast.error("Failed to generate PDF", { id: t });
                  }
                }}
                className="rounded-sm bg-slate-900 text-white hover:bg-slate-800"
              >
                <ShieldCheck className="mr-2 h-3.5 w-3.5" />
                Admin Manual
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Career site embed */}
      <div className="mt-8 space-y-4">
        <EmbedSnippet
          title="Career site embed"
          description="Paste this into your company website. Candidates see every open role and can upload their résumé — each application is AI-scored against the role automatically."
          publicUrl={`${window.location.origin}/c/${user.id}`}
          iframeTitle={`Open roles at ${user.company || user.name || "our team"}`}
          height={720}
          testIdPrefix="settings-embed"
        />
        <EmbedSnippet
          title="Application status check embed"
          description="Lets applicants paste their reference number + email and see exactly where they are in your hiring pipeline. Reduces inbound 'any update?' emails."
          publicUrl={`${window.location.origin}/c/${user.id}/status`}
          iframeTitle={`Check your application status at ${user.company || user.name || "our team"}`}
          height={620}
          testIdPrefix="settings-status-embed"
        />
      </div>

      {/* Auto-emails to candidates on rejection/withdrawal */}
      <form
        onSubmit={saveCandidateEmails}
        data-testid="settings-candidate-emails-form"
        className="mt-8 border border-slate-200"
      >
        <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
          <BellRing className="h-3 w-3" />
          Auto-emails to candidates
        </div>
        <div className="space-y-4 p-5">
          <p className="text-xs text-slate-600">
            When you change an applicant's status to one of the closed stages,
            we can send them a branded, polite email so they aren't left
            wondering. Off by default.
          </p>
          <label
            data-testid="settings-email-nothired-row"
            className="flex items-start gap-3 text-sm text-black"
          >
            <input
              type="checkbox"
              data-testid="settings-email-nothired"
              checked={emailNotHired}
              onChange={(e) => setEmailNotHired(e.target.checked)}
              className="mt-1 h-4 w-4 cursor-pointer accent-blue-700"
            />
            <span>
              <span className="font-semibold">Send a "moving forward with others" email</span>
              {" "}when status changes to <span className="rounded-sm bg-rose-50 px-1.5 py-0.5 font-mono text-[10px] font-bold text-rose-700">Not Hired</span>
              <span className="block text-xs text-slate-500">
                Polite rejection from the company side. Keeps your reputation in
                the candidate pool.
              </span>
            </span>
          </label>
          {emailNotHired && (
            <div
              data-testid="settings-template-nothired-block"
              className="ml-7 border-l-2 border-rose-200 pl-4"
            >
              <div className="flex items-center justify-between">
                <Label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
                  Email body (Not Hired)
                </Label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    data-testid="settings-template-nothired-preview"
                    onClick={() => previewEmail("not_hired")}
                    className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-blue-700 hover:underline"
                  >
                    <Eye className="h-3 w-3" /> Preview
                  </button>
                  <button
                    type="button"
                    data-testid="settings-template-nothired-testsend"
                    onClick={() => testSendEmail("not_hired")}
                    disabled={testSending === "not_hired"}
                    className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-blue-700 hover:underline disabled:opacity-60"
                  >
                    <Send className="h-3 w-3" />
                    {testSending === "not_hired" ? "Sending…" : "Send test to me"}
                  </button>
                  <button
                    type="button"
                    data-testid="settings-template-nothired-reset"
                    onClick={() => setBodyNotHired("")}
                    className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900"
                  >
                    Reset to default
                  </button>
                </div>
              </div>
              <textarea
                data-testid="settings-template-nothired"
                value={bodyNotHired}
                onChange={(e) => setBodyNotHired(e.target.value)}
                placeholder={DEFAULT_BODY_NOT_HIRED}
                rows={6}
                className="mt-1.5 w-full resize-y border border-slate-300 bg-white p-3 font-mono text-xs leading-relaxed text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <div className="mt-1 text-[10px] text-slate-500">
                Tokens you can use:{" "}
                <code className="rounded-sm bg-slate-100 px-1 font-mono">{"{name}"}</code>{" "}
                <code className="rounded-sm bg-slate-100 px-1 font-mono">{"{position}"}</code>{" "}
                <code className="rounded-sm bg-slate-100 px-1 font-mono">{"{employer}"}</code>{" "}
                <code className="rounded-sm bg-slate-100 px-1 font-mono">{"{ref}"}</code>
                {" "}· Leave blank to use the default template.
              </div>
            </div>
          )}
          <label
            data-testid="settings-email-notinterested-row"
            className="flex items-start gap-3 text-sm text-black"
          >
            <input
              type="checkbox"
              data-testid="settings-email-notinterested"
              checked={emailNotInterested}
              onChange={(e) => setEmailNotInterested(e.target.checked)}
              className="mt-1 h-4 w-4 cursor-pointer accent-blue-700"
            />
            <span>
              <span className="font-semibold">Send a withdrawal confirmation</span>
              {" "}when status changes to <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-bold text-slate-700">Not Interested</span>
              <span className="block text-xs text-slate-500">
                Acknowledges the candidate's own withdrawal. Mentions they can
                re-apply anytime.
              </span>
            </span>
          </label>
          {emailNotInterested && (
            <div
              data-testid="settings-template-notinterested-block"
              className="ml-7 border-l-2 border-slate-200 pl-4"
            >
              <div className="flex items-center justify-between">
                <Label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
                  Email body (Not Interested)
                </Label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    data-testid="settings-template-notinterested-preview"
                    onClick={() => previewEmail("not_interested")}
                    className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-blue-700 hover:underline"
                  >
                    <Eye className="h-3 w-3" /> Preview
                  </button>
                  <button
                    type="button"
                    data-testid="settings-template-notinterested-testsend"
                    onClick={() => testSendEmail("not_interested")}
                    disabled={testSending === "not_interested"}
                    className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-blue-700 hover:underline disabled:opacity-60"
                  >
                    <Send className="h-3 w-3" />
                    {testSending === "not_interested" ? "Sending…" : "Send test to me"}
                  </button>
                  <button
                    type="button"
                    data-testid="settings-template-notinterested-reset"
                    onClick={() => setBodyNotInterested("")}
                    className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900"
                  >
                    Reset to default
                  </button>
                </div>
              </div>
              <textarea
                data-testid="settings-template-notinterested"
                value={bodyNotInterested}
                onChange={(e) => setBodyNotInterested(e.target.value)}
                placeholder={DEFAULT_BODY_NOT_INTERESTED}
                rows={6}
                className="mt-1.5 w-full resize-y border border-slate-300 bg-white p-3 font-mono text-xs leading-relaxed text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <div className="mt-1 text-[10px] text-slate-500">
                Tokens you can use:{" "}
                <code className="rounded-sm bg-slate-100 px-1 font-mono">{"{name}"}</code>{" "}
                <code className="rounded-sm bg-slate-100 px-1 font-mono">{"{position}"}</code>{" "}
                <code className="rounded-sm bg-slate-100 px-1 font-mono">{"{employer}"}</code>{" "}
                <code className="rounded-sm bg-slate-100 px-1 font-mono">{"{ref}"}</code>
                {" "}· Leave blank to use the default template.
              </div>
            </div>
          )}
          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              data-testid="settings-candidate-emails-save"
              disabled={savingCandidateEmails}
              className="rounded-sm bg-blue-700 hover:bg-blue-600"
            >
              <Save className="mr-2 h-4 w-4" />
              {savingCandidateEmails ? "Saving…" : "Save preferences"}
            </Button>
          </div>
        </div>
      </form>

      {/* Billing notifications */}
      <form
        onSubmit={saveNotify}
        data-testid="settings-billing-notify-form"
        className="mt-8 border border-slate-200"
      >
        <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
          <CreditCard className="h-3 w-3" />
          Billing notifications
        </div>
        <div className="space-y-4 p-5">
          <label
            className="flex items-start gap-3 text-sm text-black"
            data-testid="settings-notify-paid-row"
          >
            <input
              type="checkbox"
              data-testid="settings-notify-paid"
              checked={notifyPaid}
              onChange={(e) => setNotifyPaid(e.target.checked)}
              className="mt-1 h-4 w-4 cursor-pointer accent-blue-700"
            />
            <span>
              <strong>Email me when an invoice is paid.</strong> A clean
              receipt arrives in your inbox, with View &amp; PDF links.
            </span>
          </label>
          <label
            className="flex items-start gap-3 text-sm text-black"
            data-testid="settings-notify-failed-row"
          >
            <input
              type="checkbox"
              data-testid="settings-notify-failed"
              checked={notifyFailed}
              onChange={(e) => setNotifyFailed(e.target.checked)}
              className="mt-1 h-4 w-4 cursor-pointer accent-blue-700"
            />
            <span>
              <strong>Email me when a payment fails.</strong> Update your card
              before access is interrupted.
            </span>
          </label>
          <div className="grid gap-2">
            <Label
              htmlFor="settings-notify-cc"
              className="text-xs font-bold uppercase tracking-[0.18em] text-slate-700"
            >
              CC emails (optional, up to 5)
            </Label>
            <Input
              id="settings-notify-cc"
              data-testid="settings-notify-cc"
              value={notifyCc}
              onChange={(e) => setNotifyCc(e.target.value)}
              placeholder="accounting@company.com, cfo@company.com"
              className="rounded-sm border-slate-300"
            />
            <p className="text-[11px] text-slate-500">
              Comma-separated. These addresses are CC'd on every billing
              notification you've turned on above.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3">
          <Button
            type="submit"
            disabled={savingNotify}
            data-testid="settings-notify-save-btn"
            size="sm"
            className="rounded-sm bg-blue-700 text-white hover:bg-blue-600"
          >
            <Save className="mr-2 h-3.5 w-3.5" />
            {savingNotify ? "Saving…" : "Save preferences"}
          </Button>
        </div>
      </form>

      {/* Password */}
      <form
        onSubmit={savePassword}
        data-testid="settings-password-form"
        className="mt-8 border border-slate-200"
      >
        <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
          <KeyRound className="h-3 w-3" />
          Password
        </div>
        <div className="space-y-3 p-5">
          <div className="grid gap-2">
            <Label htmlFor="settings-new-password" className="text-slate-900">New password</Label>
            <Input
              id="settings-new-password"
              data-testid="settings-password-input"
              type="password"
              placeholder="At least 6 characters"
              value={form.password}
              onChange={(e) =>
                setForm({ ...form, password: e.target.value })
              }
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3">
          <Button
            type="submit"
            disabled={savingPassword || !form.password}
            data-testid="settings-password-save-btn"
            size="sm"
            className="rounded-sm bg-red-600 text-white hover:bg-red-700"
          >
            <Save className="mr-2 h-3.5 w-3.5" />
            {savingPassword ? "Updating…" : "Update password"}
          </Button>
        </div>
      </form>
      <ManualSearchModal
        open={!!manualSearchKind}
        kind={manualSearchKind || "employer"}
        onClose={() => setManualSearchKind(null)}
      />
    </main>

    <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
      <DialogContent
        data-testid="settings-template-preview-dialog"
        className="max-w-2xl"
      >
        <DialogHeader>
          <DialogTitle>Email preview</DialogTitle>
          <DialogDescription>
            This is exactly what the candidate will receive — with sample
            values filled in. No real email is sent.
          </DialogDescription>
        </DialogHeader>
        {previewLoading || !previewData ? (
          <div className="py-12 text-center text-sm text-slate-500">
            Rendering…
          </div>
        ) : (
          <div data-testid="settings-template-preview-body" className="space-y-3">
            <div className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-1 border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
              <div className="font-semibold uppercase tracking-[0.18em] text-slate-500">From</div>
              <div className="font-mono text-slate-800">{previewData.from}</div>
              <div className="font-semibold uppercase tracking-[0.18em] text-slate-500">To</div>
              <div className="font-mono text-slate-800">{previewData.to_sample}</div>
              <div className="font-semibold uppercase tracking-[0.18em] text-slate-500">Subject</div>
              <div className="text-slate-900">{previewData.subject}</div>
            </div>
            <div
              data-testid="settings-template-preview-html"
              className="max-h-[420px] overflow-auto border border-slate-200 bg-slate-50"
              dangerouslySetInnerHTML={{ __html: previewData.html }}
            />
            {previewData.using_default && (
              <div className="text-[11px] text-slate-500">
                You haven't customized this template — preview uses the built-in default.
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
